import { useLocalization } from '@lib/localization';
import { countPoiNotices, noticeLabelKeys, noticeTypesBySeverity } from '@lib/spproject/inspectionStatus';
import { type Inspection } from '@lib/spproject/types';
import { Box, Tooltip, Typography } from '@mui/material';
import useNoticeColors from '@store/hooks/useNoticeColors';
import { memo, useMemo } from 'react';

interface Props {
  pois?: Inspection.PointOfInterest[];
}

/**
 * How an inspection's points stand, at a glance: a count per notice, worst first, and only
 * for notices that actually occur — an inspection with no errors should not have to say so.
 * Dots rather than chips because this sits in a row that already carries several.
 */
const InspectionStatusSummary = memo<Props>(({ pois }) => {
  const { t } = useLocalization();
  const noticeColors = useNoticeColors();

  const counts = useMemo(() => countPoiNotices(pois), [pois]);

  const labels: Record<Inspection.NoticeType, string> = {
    Error: t(noticeLabelKeys.Error),
    Warning: t(noticeLabelKeys.Warning),
    NotChecked: t(noticeLabelKeys.NotChecked),
    Ok: t(noticeLabelKeys.Ok),
  };

  const present = noticeTypesBySeverity.filter((noticeType) => counts[noticeType] > 0);

  if (!present.length) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {present.map((noticeType) => (
        <Tooltip key={noticeType} title={labels[noticeType]}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: noticeColors[noticeType] }} />
            <Typography variant="caption" color="text.secondary">
              {counts[noticeType]}
            </Typography>
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
});

export default InspectionStatusSummary;
